import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { UseCaseBasic } from "./types"

interface UseCaseCardProps {
  /** Use case data to display */
  useCase: UseCaseBasic
  /** Animation delay index for staggered animations */
  index?: number
}

/**
 * Returns the appropriate badge styling based on the tag name.
 * Qatar/GCC tags get primary green, Tech tags get secondary orange,
 * and other tags get neutral gray.
 */
function getTagBadgeClasses(tag: string): string {
  const regionTags = ["Qatar", "GCC", "UAE"]
  const techTags = ["Tech", "SaaS", "Cloud", "Analytics"]

  if (regionTags.includes(tag)) {
    return "border-primary/30 bg-primary/10 text-primary"
  }
  if (techTags.includes(tag)) {
    return "border-secondary/30 bg-secondary/10 text-secondary"
  }
  return "border-neutral-300 bg-neutral-100 text-neutral-600"
}

/**
 * Use Case Card component for the listing page grid.
 * Shows title, description, tags, and optionally links to detail page.
 */
export function UseCaseCard({ useCase, index = 0 }: UseCaseCardProps) {
  const Icon = useCase.icon
  const cardContent = (
    <>
      {/* Hover gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative">
        {/* Icon */}
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 ring-1 ring-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>

        {/* Title */}
        <h3 className="mb-2 text-lg font-semibold text-neutral-900">
          {useCase.title}
        </h3>

        {/* Description/Tagline */}
        <p className="mb-4 text-sm leading-relaxed text-neutral-600">
          {useCase.tagline}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {useCase.tags.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className={cn("text-xs font-medium", getTagBadgeClasses(tag))}
            >
              {tag}
            </Badge>
          ))}
        </div>

        {/* View Details indicator for pages with dedicated pages */}
        {useCase.hasDedicatedPage && (
          <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            View Details
            <ArrowRight className="h-4 w-4" />
          </div>
        )}

        {/* Arrow indicator on hover */}
        <div className="absolute right-0 top-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <ArrowRight className="h-5 w-5 text-primary" />
        </div>
      </div>
    </>
  )

  const cardClasses = cn(
    "group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-neutral-200/80 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-primary/20",
    useCase.hasDedicatedPage && "cursor-pointer"
  )

  if (useCase.hasDedicatedPage) {
    return (
      <Link
        to={`/use-cases/${useCase.slug}`}
        className={cardClasses}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {cardContent}
      </Link>
    )
  }

  return (
    <div className={cardClasses} style={{ animationDelay: `${index * 50}ms` }}>
      {cardContent}
    </div>
  )
}
